import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  ChevronRight, Wrench, Clock, CheckCircle, FileText, BarChart3,
  DollarSign, Activity, AlertTriangle, Loader2, Search, X,
  ClipboardCheck, Truck, MapPin, Calendar, Shield
} from 'lucide-react';
import { getCMDashboard, getPMDashboard } from '../api';

const COLORS = ['#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c', '#0d7a8a', '#14b8c4', '#8965e0', '#06b6d4', '#0a3845'];

const STATUS_COLORS = {
  'Pengajuan': '#fb6340',
  'Menunggu Approval': '#f5a623',
  'Diproses': '#11cdef',
  'Menunggu Sparepart': '#8965e0',
  'Sedang Dikerjakan': '#5e72e4',
  'Siap Selesai': '#2dce89',
  'Selesai': '#2dce89',
  'Unknown': '#8898aa',
};

/* ═══════════════════════ SHARED COMPONENTS ═══════════════════════ */

function StatCard({ icon: Icon, label, value, color, sublabel, format }) {
  const displayValue = format === 'currency'
    ? `Rp ${(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}`
    : format === 'decimal'
    ? (value || 0).toFixed(1)
    : (value || 0).toLocaleString();
  return (
    <div className="stat-card" style={{
      background: '#fff', borderRadius: 12, padding: '18px 20px',
      border: `1px solid ${color}20`, borderTop: `3px solid ${color}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: color + '15', color: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--gray-800)', lineHeight: 1.2 }}>{displayValue}</div>
          {sublabel && <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 2 }}>{sublabel}</div>}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`chart-card ${className}`} style={{
      background: '#fff', borderRadius: 12, border: '1px solid var(--gray-100)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden',
    }}>
      <div className="chart-card-header" style={{
        padding: '14px 18px 8px',
        borderBottom: '1px solid var(--gray-50)',
      }}>
        <h6 className="chart-card-title" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>{title}</h6>
        {subtitle && <span className="chart-card-subtitle" style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>{subtitle}</span>}
      </div>
      <div className="chart-card-body" style={{ padding: '12px 16px 16px' }}>
        {children}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{
        background: '#fff', borderRadius: 8, padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef',
      }}>
        <p className="tooltip-label" style={{ fontWeight: 600, fontSize: '0.8rem', margin: '0 0 4px' }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="tooltip-item" style={{ color: p.color, fontSize: '0.78rem', margin: 0 }}>
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

function ProgressBarList({ data, colorKey = 'name', valueKey = 'count', labelKey }) {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.map((d, i) => {
        const pct = (d[valueKey] / maxVal) * 100;
        const c = COLORS[i % COLORS.length];
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 0', transition: 'transform 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {labelKey ? d[labelKey] : d[colorKey]}
              </span>
            </div>
            <div style={{ flex: 2, height: 6, borderRadius: 3, background: '#f1f3f5', overflow: 'hidden', maxWidth: 120 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${c}, ${c}88)`, borderRadius: 3, transition: 'width 0.5s ease' }} />
            </div>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: c, minWidth: 28, textAlign: 'right' }}>{d[valueKey]}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════ PAGE HEADER ═══════════════════════ */

function PageHeader({ activeTab }) {
  const gradient = activeTab === 'cm'
    ? 'linear-gradient(135deg, #5e72e4 0%, #324cdd 50%, #172b4d 100%)'
    : 'linear-gradient(135deg, #2dce89 0%, #1aae6f 50%, #0a5c36 100%)';
  const iconBg = activeTab === 'cm' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.15)';

  return (
    <div className="dash-header" style={{ position: 'relative', marginBottom: 20 }}>
      <div className="dash-header-bg" style={{
        background: gradient,
        borderRadius: '0 0 24px 24px', padding: '24px 28px', position: 'relative',
      }}>
        <div className="dash-header-content">
          <div className="dash-breadcrumb" style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>
            <span>Maintenance</span>
            <span className="dash-breadcrumb-sep" style={{ margin: '0 6px' }}>/</span>
            <span className="dash-breadcrumb-current" style={{ color: '#fff' }}>Dashboard {activeTab === 'cm' ? 'CM' : 'PM'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart3 size={26} color="#fff" />
            </div>
            <div>
              <h2 className="dash-title" style={{ color: '#fff', margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>
                Dashboard {activeTab === 'cm' ? 'Corrective Maintenance' : 'Preventive Maintenance'}
              </h2>
              <p className="dash-subtitle" style={{ color: 'rgba(255,255,255,0.7)', margin: '2px 0 0', fontSize: '0.85rem' }}>
                {activeTab === 'cm' ? 'Ringkasan statistik dan performa perbaikan' : 'Ringkasan jadwal pemeriksaan berkala kendaraan'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ CM DASHBOARD CONTENT ═══════════════════════ */

function PendingApprovalAlert({ stats, navigate }) {
  const pending = stats.pending_approval || [];
  if (pending.length === 0) return null;

  return (
    <div className="pending-alert" style={{
      background: 'linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)',
      borderRadius: 12, border: '1.5px solid #ffc107',
      boxShadow: '0 2px 8px rgba(255,193,7,0.2)',
      marginBottom: 20, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderBottom: '1px solid rgba(255,193,7,0.3)',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: '#ffc107',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AlertTriangle size={20} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#856404' }}>
            {pending.length} CM menunggu approval lebih dari 24 jam
          </div>
          <div style={{ fontSize: '0.72rem', color: '#b8860b', marginTop: 1 }}>
            Segera proses pengajuan berikut
          </div>
        </div>
      </div>
      <div style={{ padding: '4px 0' }}>
        {pending.map((item, idx) => {
          const days = item.waiting_days || 0;
          const hours = item.waiting_hours || 0;
          const isUrgent = days >= 3;
          const isWarning = days >= 1;

          let bgColor, badgeColor, badgeText;
          if (isUrgent) {
            bgColor = '#fff5f5'; badgeColor = '#f5365c'; badgeText = 'URGENT';
          } else if (isWarning) {
            bgColor = '#fffef5'; badgeColor = '#f5a623'; badgeText = 'MENUNGGU';
          } else {
            bgColor = '#f8f9fa'; badgeColor = '#8898aa'; badgeText = 'BARU';
          }

          const waitingLabel = days > 0
            ? `${days} hari${hours > 0 ? ` ${hours} jam` : ''}`
            : `${hours} jam`;

          return (
            <div key={item.id || idx} onClick={() => navigate('/cm')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px', cursor: 'pointer',
                borderBottom: idx < pending.length - 1 ? '1px solid rgba(255,193,7,0.2)' : 'none',
                background: bgColor,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = (isUrgent ? '#ffe0e0' : '#fff3cd')}
              onMouseLeave={e => e.currentTarget.style.background = bgColor}>
              <span style={{
                fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.5px',
                padding: '2px 6px', borderRadius: 4,
                background: badgeColor + '18', color: badgeColor,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>{badgeText}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                  {item.cm_id}
                  {item.nama_teknisi && (
                    <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: 6 }}>
                      — {item.nama_teknisi}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 1 }}>
                  {item.jenis_pekerjaan || '—'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{
                  fontSize: '0.75rem', fontWeight: 700,
                  color: isUrgent ? '#f5365c' : '#f5a623',
                }}>{waitingLabel}</div>
                <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)' }}>menunggu</div>
              </div>
              <ChevronRight size={14} color="var(--gray-300)" style={{ flexShrink: 0 }} />
            </div>
          );
        })}
      </div>
      <div style={{
        padding: '8px 16px', textAlign: 'right',
        borderTop: '1px solid rgba(255,193,7,0.3)',
      }}>
        <button onClick={() => navigate('/cm')}
          style={{
            background: 'transparent', border: `1px solid #ffc107`,
            borderRadius: 6, padding: '4px 14px',
            fontSize: '0.72rem', fontWeight: 600, color: '#856404',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#ffc107'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#856404'; }}>
          Lihat Semua Pengajuan →
        </button>
      </div>
    </div>
  );
}

function CMContent({ stats }) {
  const navigate = useNavigate();
  const statusData = (stats.per_status || []).map(s => ({
    ...s,
    fill: STATUS_COLORS[s.status] || '#8898aa',
  }));

  const totalInProgress = stats.total_diproses || 0;
  const totalPending = stats.total_menunggu || 0;
  const totalDone = stats.total_selesai || 0;
  const completionRate = stats.total_cm > 0 ? Math.round((totalDone / stats.total_cm) * 100) : 0;

  return (
    <>
      <PendingApprovalAlert stats={stats} navigate={navigate} />

      {/* STAT CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
        <StatCard icon={FileText} label="Total CM" value={stats.total_cm} color="#5e72e4" sublabel="Semua laporan" />
        <StatCard icon={Clock} label="Menunggu / Pengajuan" value={totalPending} color="#fb6340" sublabel="Perlu diproses" />
        <StatCard icon={Wrench} label="Dalam Proses" value={totalInProgress} color="#11cdef" sublabel="Sedang dikerjakan" />
        <StatCard icon={CheckCircle} label="Selesai" value={totalDone} color="#2dce89" sublabel={`${completionRate}% tingkat penyelesaian`} />
        <StatCard icon={DollarSign} label="Total Tagihan" value={stats.total_nominal || 0} color="#f5365c" sublabel="Dari CM selesai" format="currency" />
        <StatCard icon={BarChart3} label="Rata-rata Progress" value={Math.round(stats.avg_progress || 0)} color="#8965e0" sublabel="CM yang sedang berjalan" />
      </div>

      {/* CHARTS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16, marginBottom: 20 }}>
        {/* Status Distribution - Pie */}
        <ChartCard title="Status CM" subtitle={`${stats.total_cm} total laporan`}>
          {statusData.length > 0 ? (
            <div className="donut-wrapper" style={{ display: 'flex', justifyContent: 'center', gap: 20, alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 200, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <filter id="donutShadow">
                        <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.08" />
                      </filter>
                    </defs>
                    <Pie
                      data={statusData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3}
                      dataKey="count"
                      filter="url(#donutShadow)"
                    >
                      {statusData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  textAlign: 'center', pointerEvents: 'none',
                }}>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#5e72e4', lineHeight: 1 }}>{stats.total_cm}</div>
                  <div style={{ fontSize: '0.6rem', color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {statusData.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.fill }} />
                    <span style={{ color: 'var(--gray-600)', minWidth: 100 }}>{s.status}</span>
                    <span style={{ fontWeight: 700, color: 'var(--gray-800)' }}>{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Tidak ada data status</p>
          )}
        </ChartCard>

        {/* Monthly Trend - Line */}
        <ChartCard title="Tren Bulanan" subtitle="12 bulan terakhir">
          {(stats.monthly_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={stats.monthly_trend} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                <XAxis dataKey="bulan" stroke="#adb5bd" fontSize={10} tickFormatter={v => {
                  const parts = (v || '').split('-');
                  return parts.length === 2 ? `${parts[0].slice(2)}/${parts[1]}` : v;
                }} />
                <YAxis stroke="#adb5bd" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="count" stroke="#5e72e4" strokeWidth={2.5} dot={{ r: 4, fill: '#5e72e4', stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#5e72e4', stroke: '#fff', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data tren bulanan</p>
          )}
        </ChartCard>

        {/* Per Teknisi - Bar Chart */}
        <ChartCard title="Per Teknisi" subtitle={`${(stats.per_teknisi || []).length} teknisi teratas`}>
          {(stats.per_teknisi || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.per_teknisi} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                <XAxis dataKey="nama" stroke="#adb5bd" fontSize={9} tickFormatter={v => v && v.length > 10 ? v.slice(0, 10) + '...' : v} />
                <YAxis stroke="#adb5bd" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  {stats.per_teknisi.map((_, idx) => (
                    <linearGradient key={idx} id={`cmTekGrad${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.4} />
                    </linearGradient>
                  ))}
                </defs>
                <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={32}>
                  {stats.per_teknisi.map((_, idx) => (
                    <Cell key={idx} fill={`url(#cmTekGrad${idx})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data teknisi</p>
          )}
        </ChartCard>

        {/* Per Jenis Pekerjaan */}
        <ChartCard title="Jenis Pekerjaan" subtitle="Distribusi jenis pekerjaan CM">
          {(stats.per_jenis_pekerjaan || []).length > 0 ? (
            <ProgressBarList data={stats.per_jenis_pekerjaan} colorKey="jenis" valueKey="count" labelKey="jenis" />
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data jenis pekerjaan</p>
          )}
        </ChartCard>

        {/* Per Jenis Pengerjaan */}
        <ChartCard title="Jenis Pengerjaan" subtitle="Distribusi jenis pengerjaan (AC, CCTV, dll)">
          {(stats.per_jenis_pengerjaan || []).length > 0 ? (
            <ProgressBarList data={stats.per_jenis_pengerjaan} colorKey="jenis" valueKey="count" labelKey="jenis" />
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data jenis pengerjaan</p>
          )}
        </ChartCard>

        {/* Recent Activity */}
        <ChartCard title="Aktivitas Terbaru" subtitle="Log aktivitas CM terkini">
          {(stats.recent_activity || []).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 260, overflowY: 'auto' }}>
              {stats.recent_activity.map((log, idx) => (
                <div key={log.id || idx} style={{
                  display: 'flex', gap: 10, padding: '8px 0',
                  borderBottom: idx < stats.recent_activity.length - 1 ? '1px solid var(--gray-50)' : 'none',
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: idx === 0 ? '#5e72e4' : '#e9ecef',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2,
                  }}>
                    <Activity size={11} color={idx === 0 ? '#fff' : '#8898aa'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-700)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {log.description || '-'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, fontSize: '0.65rem', color: 'var(--gray-400)', marginTop: 3 }}>
                      <span>{log.created_at}</span>
                      {log.username && <span>— <strong>{log.username}</strong></span>}
                      {log.cm_id && <span>— <code style={{ fontSize: '0.6rem', background: '#f4f4f4', padding: '1px 4px', borderRadius: 2 }}>{log.cm_id}</code></span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Activity size={32} color="#8898aa" style={{ marginBottom: 8, opacity: 0.4 }} />
              <p className="text-muted text-sm">Belum ada aktivitas</p>
            </div>
          )}
        </ChartCard>
      </div>

      {/* SUMMARY ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 30 }}>
        <ChartCard title="Ringkasan Cepat" subtitle="Fakta singkat CM">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: 'Total CM', value: stats.total_cm, color: '#5e72e4' },
              { label: 'Pengajuan', value: totalPending, color: '#fb6340' },
              { label: 'Diproses', value: totalInProgress, color: '#11cdef' },
              { label: 'Selesai', value: totalDone, color: '#2dce89' },
            ].map((item, i) => (
              <div key={i} style={{
                background: item.color + '08', borderRadius: 8, padding: '10px 12px',
                border: `1px solid ${item.color}15`,
              }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Tingkat Penyelesaian" subtitle="Rasio CM selesai vs total">
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 12px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Selesai', value: totalDone },
                      { name: 'Belum Selesai', value: Math.max(0, stats.total_cm - totalDone) },
                    ]}
                    cx="50%" cy="50%"
                    innerRadius={45} outerRadius={65}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    <Cell fill="#2dce89" />
                    <Cell fill="#e9ecef" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none',
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#2dce89', lineHeight: 1 }}>{completionRate}%</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: '0.78rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2dce89' }} />
                <span>{totalDone} Selesai</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e9ecef' }} />
                <span>{Math.max(0, stats.total_cm - totalDone)} Belum</span>
              </div>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Informasi Lainnya" subtitle="Data tambahan">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6 }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Jenis Pekerjaan</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)' }}>{(stats.per_jenis_pekerjaan || []).length} jenis</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6 }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Teknisi Aktif</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)' }}>{(stats.per_teknisi || []).length} teknisi</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6 }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Total Nominal</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f5365c' }}>
                Rp {(stats.total_nominal || 0).toLocaleString('id-ID')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6 }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Rata-rata Progress</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#8965e0' }}>{Math.round(stats.avg_progress || 0)}%</span>
            </div>
          </div>
        </ChartCard>
      </div>
    </>
  );
}

/* ═══════════════════════ PM DASHBOARD CONTENT ═══════════════════════ */

function PMContent({ stats }) {
  const navigate = useNavigate();
  const completionRate = stats.total_pm > 0 ? Math.round(((stats.total_selesai || 0) / stats.total_pm) * 100) : 0;

  return (
    <>
      {/* STAT CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
        <StatCard icon={ClipboardCheck} label="Total PM" value={stats.total_pm} color="#2dce89" sublabel="Semua pemeriksaan" />
        <StatCard icon={CheckCircle} label="Selesai" value={stats.total_selesai || 0} color="#5e72e4" sublabel={`${completionRate}% selesai`} />
        <StatCard icon={Truck} label="Kendaraan Unik" value={stats.vehicle_count || 0} color="#11cdef" sublabel="Telah diperiksa" />
        <StatCard icon={Calendar} label="Rata-rata/Hari" value={stats.avg_per_day || 0} color="#f59e0b" sublabel="PM per hari" format="decimal" />
        <StatCard icon={MapPin} label="Total Rute" value={(stats.per_rute || []).length} color="#8965e0" sublabel="Rute aktif" />
        <StatCard icon={Shield} label="Teknisi Aktif" value={(stats.per_teknisi || []).length} color="#fb6340" sublabel="Melakukan PM" />
      </div>

      {/* CHARTS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16, marginBottom: 20 }}>

        {/* Daily Trend - Line */}
        <ChartCard title="Tren Harian (30 Hari)" subtitle="Jumlah PM per hari">
          {(stats.daily_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={stats.daily_trend} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                <XAxis dataKey="tanggal" stroke="#adb5bd" fontSize={9} tickFormatter={v => {
                  const parts = (v || '').split('-');
                  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : v;
                }} />
                <YAxis stroke="#adb5bd" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="count" name="Jumlah PM" stroke="#2dce89" strokeWidth={2.5}
                  dot={{ r: 3, fill: '#2dce89', stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#2dce89', stroke: '#fff', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data tren harian</p>
          )}
        </ChartCard>

        {/* Monthly Trend */}
        <ChartCard title="Tren Bulanan" subtitle="Total PM per bulan">
          {(stats.monthly_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.monthly_trend} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" vertical={false} />
                <XAxis dataKey="bulan" stroke="#adb5bd" fontSize={10} tickFormatter={v => {
                  const parts = (v || '').split('-');
                  return parts.length === 2 ? `${parts[0].slice(2)}/${parts[1]}` : v;
                }} />
                <YAxis stroke="#adb5bd" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  <linearGradient id="pmMonthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2dce89" stopOpacity={1} />
                    <stop offset="100%" stopColor="#2dce89" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <Bar dataKey="count" name="Jumlah PM" fill="url(#pmMonthGrad)" radius={[6, 6, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data tren bulanan</p>
          )}
        </ChartCard>

        {/* Per Teknisi */}
        <ChartCard title="PM per Teknisi" subtitle={`${(stats.per_teknisi || []).length} teknisi aktif`}>
          {(stats.per_teknisi || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.per_teknisi} margin={{ top: 10, right: 10, left: -10, bottom: 5 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f3f5" horizontal={false} />
                <XAxis type="number" stroke="#adb5bd" fontSize={11} allowDecimals={false} />
                <YAxis dataKey="nama" type="category" stroke="#adb5bd" fontSize={10} width={120}
                  tickFormatter={v => v && v.length > 15 ? v.slice(0, 15) + '...' : v} />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  {stats.per_teknisi.map((_, idx) => (
                    <linearGradient key={idx} id={`pmTekGrad${idx}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={1} />
                    </linearGradient>
                  ))}
                </defs>
                <Bar dataKey="count" name="Jumlah PM" radius={[0, 6, 6, 0]} barSize={20}>
                  {stats.per_teknisi.map((_, idx) => (
                    <Cell key={idx} fill={`url(#pmTekGrad${idx})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data teknisi</p>
          )}
        </ChartCard>

        {/* Per Rute */}
        <ChartCard title="PM per Rute" subtitle={`${(stats.per_rute || []).length} rute aktif`}>
          {(stats.per_rute || []).length > 0 ? (
            <ProgressBarList data={stats.per_rute} colorKey="rute" valueKey="count" labelKey="rute" />
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data rute</p>
          )}
        </ChartCard>

        {/* Per Pengawas */}
        <ChartCard title="PM per Pengawas" subtitle="Distribusi pengawasan">
          {(stats.per_pengawas || []).length > 0 ? (
            <div className="donut-wrapper" style={{ display: 'flex', justifyContent: 'center', gap: 20, alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 180, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.per_pengawas}
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={75}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="nama"
                    >
                      {stats.per_pengawas.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  textAlign: 'center', pointerEvents: 'none',
                }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#2dce89', lineHeight: 1 }}>{stats.total_pm}</div>
                  <div style={{ fontSize: '0.55rem', color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {stats.per_pengawas.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
                    <span style={{ color: 'var(--gray-600)', minWidth: 100 }}>{p.nama}</span>
                    <span style={{ fontWeight: 700, color: 'var(--gray-800)' }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data pengawas</p>
          )}
        </ChartCard>

        {/* Recent PM Activity */}
        <ChartCard title="PM Terbaru" subtitle="10 PM paling akhir">
          {(stats.recent_pm || []).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto' }}>
              {stats.recent_pm.map((pm, idx) => (
                <div key={pm.id || idx} style={{
                  display: 'flex', gap: 10, padding: '8px 0',
                  borderBottom: idx < stats.recent_pm.length - 1 ? '1px solid var(--gray-50)' : 'none',
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                  onClick={() => navigate('/pm')}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: '#2dce8915',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2,
                  }}>
                    <ClipboardCheck size={13} color="#2dce89" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                      {pm.pm_id || `PM#${pm.id}`}
                      <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: 6 }}>
                        — {pm.teknisi || '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, fontSize: '0.65rem', color: 'var(--gray-400)', marginTop: 2 }}>
                      <span>{pm.no_body || pm.rute || '—'}</span>
                      {pm.rute && <span>• {pm.rute}</span>}
                      <span>{pm.tanggal}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} color="var(--gray-300)" style={{ flexShrink: 0, marginTop: 6 }} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <ClipboardCheck size={32} color="#8898aa" style={{ marginBottom: 8, opacity: 0.4 }} />
              <p className="text-muted text-sm">Belum ada data PM</p>
            </div>
          )}
        </ChartCard>
      </div>

      {/* SUMMARY ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 30 }}>
        <ChartCard title="Ringkasan Cepat PM" subtitle="Fakta singkat Preventive Maintenance">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: 'Total PM', value: stats.total_pm, color: '#2dce89' },
              { label: 'Selesai', value: stats.total_selesai || 0, color: '#5e72e4' },
              { label: 'Kendaraan', value: stats.vehicle_count || 0, color: '#11cdef' },
              { label: 'Rute Aktif', value: (stats.per_rute || []).length, color: '#8965e0' },
            ].map((item, i) => (
              <div key={i} style={{
                background: item.color + '08', borderRadius: 8, padding: '10px 12px',
                border: `1px solid ${item.color}15`,
              }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Tingkat Penyelesaian PM" subtitle="Rasio PM selesai vs total">
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 12px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Selesai', value: stats.total_selesai || 0 },
                      { name: 'Belum', value: Math.max(0, (stats.total_pm || 0) - (stats.total_selesai || 0)) },
                    ]}
                    cx="50%" cy="50%"
                    innerRadius={45} outerRadius={65}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    <Cell fill="#2dce89" />
                    <Cell fill="#e9ecef" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none',
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#2dce89', lineHeight: 1 }}>{completionRate}%</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: '0.78rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2dce89' }} />
                <span>{stats.total_selesai || 0} Selesai</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e9ecef' }} />
                <span>{Math.max(0, (stats.total_pm || 0) - (stats.total_selesai || 0))} Belum</span>
              </div>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Distribusi Teknisi" subtitle="Top teknisi PM">
          {(stats.per_teknisi || []).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats.per_teknisi.slice(0, 5).map((t, i) => {
                const pct = stats.total_pm > 0 ? Math.round((t.count / stats.total_pm) * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: COLORS[i % COLORS.length] + '20',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', fontWeight: 700, color: COLORS[i % COLORS.length],
                      flexShrink: 0,
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.nama}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>{t.count} PM • {pct}%</div>
                    </div>
                    <div style={{ width: 60, height: 6, borderRadius: 3, background: '#f1f3f5', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted text-sm" style={{ textAlign: 'center', padding: 20 }}>Belum ada data</p>
          )}
        </ChartCard>
      </div>
    </>
  );
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */

export default function MaintenanceDashboard() {
  const [activeTab, setActiveTab] = useState('cm');
  const [cmStats, setCmStats] = useState(null);
  const [pmStats, setPmStats] = useState(null);
  const [cmLoading, setCmLoading] = useState(true);
  const [pmLoading, setPmLoading] = useState(true);
  const [cmError, setCmError] = useState('');
  const [pmError, setPmError] = useState('');
  const [tanggalAwal, setTanggalAwal] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [tanggalAkhir, setTanggalAkhir] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });

  const fetchCM = useCallback(() => {
    setCmLoading(true);
    setCmError('');
    const params = {};
    if (tanggalAwal) params.tgl_awal = tanggalAwal;
    if (tanggalAkhir) params.tgl_akhir = tanggalAkhir;
    getCMDashboard(params)
      .then(res => setCmStats(res.data))
      .catch(err => {
        console.error('Failed to load CM dashboard', err);
        setCmError('Gagal memuat data dashboard CM.');
      })
      .finally(() => setCmLoading(false));
  }, [tanggalAwal, tanggalAkhir]);

  const fetchPM = useCallback(() => {
    setPmLoading(true);
    setPmError('');
    const params = {};
    if (tanggalAwal) params.tgl_awal = tanggalAwal;
    if (tanggalAkhir) params.tgl_akhir = tanggalAkhir;
    getPMDashboard(params)
      .then(res => setPmStats(res.data))
      .catch(err => {
        console.error('Failed to load PM dashboard', err);
        setPmError('Gagal memuat data dashboard PM.');
      })
      .finally(() => setPmLoading(false));
  }, [tanggalAwal, tanggalAkhir]);

  useEffect(() => { fetchCM(); }, [fetchCM]);
  useEffect(() => { fetchPM(); }, [fetchPM]);

  const isLoading = activeTab === 'cm' ? cmLoading : pmLoading;
  const error = activeTab === 'cm' ? cmError : pmError;
  const stats = activeTab === 'cm' ? cmStats : pmStats;

  return (
    <div className="dash-wrapper">
      <PageHeader activeTab={activeTab} />

      <div className="dash-content" style={{ padding: '0 24px' }}>
        {/* TAB NAVIGATION */}
        <div style={{
          display: 'flex', gap: 0, marginBottom: 16,
          background: '#fff', borderRadius: 10,
          border: '1px solid var(--gray-100)',
          overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          {[
            { key: 'cm', label: 'Corrective Maintenance', icon: Wrench, color: '#5e72e4', desc: 'Perbaikan & troubleshooting' },
            { key: 'pm', label: 'Preventive Maintenance', icon: ClipboardCheck, color: '#2dce89', desc: 'Pemeriksaan berkala' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flex: 1, padding: '12px 20px', border: 'none', cursor: 'pointer',
              background: activeTab === tab.key
                ? `linear-gradient(135deg, ${tab.color}12, ${tab.color}08)`
                : 'transparent',
              borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
              onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.background = tab.color + '06'; }}
              onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: activeTab === tab.key ? tab.color + '20' : 'var(--gray-100)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}>
                <tab.icon size={17} color={activeTab === tab.key ? tab.color : 'var(--gray-400)'} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontSize: '0.82rem', fontWeight: activeTab === tab.key ? 700 : 500,
                  color: activeTab === tab.key ? tab.color : 'var(--gray-500)',
                  transition: 'color 0.2s',
                }}>{tab.label}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', marginTop: 1 }}>{tab.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* ═══════ DATE FILTER BAR ═══════ */}
        <div className="filter-bar" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 16, padding: '8px 14px',
          background: '#fff', borderRadius: 10,
          border: '1px solid var(--gray-100)',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
            <Search size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Filter Periode:
          </span>
          <input type="date" value={tanggalAwal} onChange={e => setTanggalAwal(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.78rem', background: '#fff', maxWidth: 140 }}
            placeholder="Tgl. awal" />
          <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>s/d</span>
          <input type="date" value={tanggalAkhir} onChange={e => setTanggalAkhir(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.78rem', background: '#fff', maxWidth: 140 }}
            placeholder="Tgl. akhir" />
          {(tanggalAwal || tanggalAkhir) && (
            <button className="btn-icon" onClick={() => { setTanggalAwal(''); setTanggalAkhir(''); }}
              style={{ color: '#f5365c', padding: '4px 8px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
              <X size={12} /> Reset
            </button>
          )}
        </div>

        {/* ═══════ CONTENT ═══════ */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gray-400)' }}>
            <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
            <p>Memuat dashboard {activeTab.toUpperCase()}...</p>
          </div>
        ) : error || !stats ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <AlertTriangle size={36} color="#f5365c" style={{ marginBottom: 12 }} />
            <p style={{ color: 'var(--gray-500)' }}>{error || `Tidak dapat memuat data dashboard ${activeTab.toUpperCase()}.`}</p>
          </div>
        ) : activeTab === 'cm' ? (
          <CMContent stats={stats} />
        ) : (
          <PMContent stats={stats} />
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
